import { jsx } from "react/jsx-runtime";
import { LayoutSections, CatalogueProductPurchaseState, Currency, ProductSelectionContext, GridLayout } from "./styles/ProductSelection/components/layouts/GridLayout/GridLayout.mjs";
import { RowLayout } from "./styles/ProductSelection/components/layouts/RowLayout/RowLayout.mjs";
import { PRODUCT_ID } from "./styles/ProductSelection/components/layouts/shared/components/Upsells/Upsells.mjs";
import { getDisplayPrices, TrackingContext } from "./styles/ProductSelection/components/elements/Expectations/RelevantApplications/RelevantApplications.mjs";
const LayoutFormats = [
  "grid",
  "grid-centered",
  "grid-with-matrix",
  "grid-with-matrix-collapsible",
  "grid-with-matrix-compact",
  "row"
];
const DEFAULT_LAYOUT_FORMAT = "grid";
const isLayoutFormat = (format) => LayoutFormats.some((layoutFormat) => layoutFormat === format);
const mapLayoutFormat = (format) => isLayoutFormat(format) ? format : DEFAULT_LAYOUT_FORMAT;
const isLayoutSection = (section) => LayoutSections.includes(section);
const mapSectionOrder = (sections) => sections.filter(isLayoutSection);
const mapLayoutDisplay = (displayLayout) => ({
  format: mapLayoutFormat(displayLayout.format),
  sortProductIds: displayLayout.sortProductIds,
  sectionOrder: mapSectionOrder(displayLayout.sectionOrder),
  productCap: {
    products: displayLayout.productCap.products ?? void 0,
    upgrades: void 0
    // @TODO: Decide if want to include this
  }
});
const mapAddOns = (addOns) => addOns ? addOns.map(mapProduct) : [];
const BannerTypes = [
  "new",
  "featured",
  "current",
  "recommended",
  "recommended-featured",
  "beta",
  "limited-time-offer"
];
const BannerVariants = ["narrow", "wide"];
const isBannerType = (type) => BannerTypes.includes(type);
const mapBannerType = (type) => type && isBannerType(type) ? type : void 0;
const DEFAULT_VARIANT$6 = "wide";
const isBannerVariant = (variant) => BannerVariants.some((bannerVariant) => bannerVariant === variant);
const mapBannerVariant = (variant) => variant && isBannerVariant(variant) ? variant : DEFAULT_VARIANT$6;
const mapBanner = (banner) => {
  if (banner == null ? void 0 : banner.enabled) {
    const type = mapBannerType(banner.type);
    const variant = mapBannerVariant(banner.variant ?? void 0);
    return type ? { type, variant } : void 0;
  }
  return void 0;
};
const mapComparatives = (comparatives) => {
  if (comparatives == null ? void 0 : comparatives.enabled) {
    return {
      content: comparatives.content.map(
        (item) => ({
          key: item.key,
          type: item.type,
          content: item.content
        })
      )
    };
  }
  return void 0;
};
const DEFAULT_VARIANT$5 = "select";
const CtaVariants = [
  "select",
  "add",
  "current",
  "upgrade",
  "current-add-on"
];
const isCtaVariant = (variant) => CtaVariants.some((ctaVariant) => ctaVariant === variant);
const mapCtaVariant = (variant) => variant && isCtaVariant(variant) ? variant : DEFAULT_VARIANT$5;
const mapCta = (cta) => {
  if (cta == null ? void 0 : cta.enabled) {
    const variant = mapCtaVariant(cta.variant ?? void 0);
    const url = cta.url ?? void 0;
    return { variant, url };
  }
  return void 0;
};
const CustomIds = [
  "ppa-content",
  "billable-units-inclusions",
  "billable-units-inclusions-2",
  "key-features"
];
const isCustomId = (id) => CustomIds.some((customId) => customId === id);
const mapCustomContent = (custom) => isCustomId(custom.id) ? custom.id : void 0;
const mapCustom = (custom) => {
  const customContent = (custom == null ? void 0 : custom.enabled) ? custom.content.map(mapCustomContent).filter((id) => id !== void 0) : void 0;
  return customContent && customContent.length > 0 ? customContent : void 0;
};
const ExpectationVariants = [
  "featured",
  "deprioritised",
  "secondary",
  "secondary-emphasised",
  "listed"
];
const DEFAULT_VARIANT$4 = "featured";
const isExpectationVariant = (variant) => ExpectationVariants.some(
  (expectationVariant) => expectationVariant === variant
);
const mapExpectationVariant = (variant) => variant && isExpectationVariant(variant) ? variant : DEFAULT_VARIANT$4;
const mapExpectations = ({
  expectations,
  expectationForDraft,
  expectationForJob,
  comparatives
}) => {
  var _a, _b, _c;
  const { applicationsCompleted: count, correlationId } = expectationForDraft ?? expectationForJob ?? {};
  const isEnabled = (expectations == null ? void 0 : expectations.enabled) !== false;
  return {
    count: typeof count === "number" && isEnabled ? count : void 0,
    unit: "applications",
    correlationId: typeof correlationId === "string" ? correlationId : void 0,
    variant: mapExpectationVariant((expectations == null ? void 0 : expectations.variant) ?? void 0),
    views: (_c = (_b = (_a = mapComparatives(comparatives)) == null ? void 0 : _a.content) == null ? void 0 : _b.find(
      (content) => content.key === "views" && content.type === "included"
    )) == null ? void 0 : _c.content
  };
};
const ImageIds = [
  "branded-upgrade",
  "urgent-hiring-immediate-start",
  "urgent-hiring-urgently-hiring",
  "entry-level",
  "no-experience",
  "example-branding-anz",
  "example-branding-asia"
];
const isImageId = (id) => ImageIds.some((imageId) => imageId === id);
const mapImageId$1 = (id) => isImageId(id) ? id : void 0;
const mapImage$1 = (image) => {
  if (image == null ? void 0 : image.enabled) {
    const imageId = mapImageId$1(image.id);
    return imageId ? { id: imageId, description: image.description ?? "" } : void 0;
  }
  return void 0;
};
const HelpTipImageIds = [
  "priority-listing-anz",
  "priority-listing-asia",
  "branding-logo-and-cover",
  "email"
];
const InclusionVariants = [
  "default",
  "deprioritised",
  "no-icon"
];
const InclusionTypes = ["included", "excluded"];
const InclusionIconIds = ["bullet", "none"];
const InclusionIconTones = ["promote", "secondary"];
const DEFAULT_VARIANT$3 = "default";
const DEFAULT_TYPE = "included";
const isHelpTipImageId = (id) => HelpTipImageIds.includes(id);
const mapImageId = (id) => id && isHelpTipImageId(id) ? id : void 0;
const mapImage = (image) => {
  const imageId = mapImageId(image.id);
  if (imageId) {
    return {
      id: imageId,
      description: image.description ?? void 0
    };
  }
  return void 0;
};
const mapHelpTip = (helpTip) => {
  if (!helpTip) return void 0;
  const content = helpTip.content;
  const image = helpTip.image ? mapImage(helpTip.image) : void 0;
  if (content) {
    return { content, image };
  }
  return void 0;
};
const isInclusionVariant = (variant) => InclusionVariants.some((inclusionVariant) => inclusionVariant === variant);
const mapInclusionVariant = (variant) => variant && isInclusionVariant(variant) ? variant : DEFAULT_VARIANT$3;
const isInclusionType = (type) => InclusionTypes.some((inclusionType) => inclusionType === type);
const mapInclusionType = (type) => type && isInclusionType(type) ? type : DEFAULT_TYPE;
const mapInclusionIconId = (id) => InclusionIconIds.find((inclusionIconId) => inclusionIconId === id);
const mapInclusionIconTone = (tone) => InclusionIconTones.find((inclusionIconTone) => inclusionIconTone === tone);
const mapInclusionIcon = (icon) => (icon == null ? void 0 : icon.id) || (icon == null ? void 0 : icon.tone) ? {
  id: mapInclusionIconId(icon.id),
  tone: mapInclusionIconTone(icon.tone)
} : void 0;
const mapInclusions = (inclusions) => {
  if (inclusions == null ? void 0 : inclusions.enabled) {
    return {
      content: inclusions.content.map((item) => ({
        name: item.content,
        type: mapInclusionType(item.type),
        icon: mapInclusionIcon(item.icon),
        helpTip: mapHelpTip(item.helpTip)
      })),
      variant: mapInclusionVariant((inclusions == null ? void 0 : inclusions.variant) ?? void 0)
    };
  }
  return void 0;
};
const mapName = (displayMetadata, productName) => (displayMetadata == null ? void 0 : displayMetadata.name) ?? productName;
const PanelVariants = [
  "default",
  "featured",
  "featured-focus",
  "highlight-background",
  "hidden"
];
const DEFAULT_VARIANT$2 = "default";
const isPanelVariant = (variant) => PanelVariants.some((panelVariant) => panelVariant === variant);
const mapPanelVariant = (variant) => variant && isPanelVariant(variant) ? variant : DEFAULT_VARIANT$2;
const mapPanel = (panel) => ({
  variant: panel ? mapPanelVariant(panel.variant ?? void 0) : DEFAULT_VARIANT$2
});
const PriceVariants = [
  "billable-units-highlight",
  "billable-units-highlight-2",
  "slim"
];
const isPriceVariant = (variant) => PriceVariants.some((priceVariant) => priceVariant === variant);
const mapPriceVariant = (variant) => variant && isPriceVariant(variant) ? variant : void 0;
const mapPrice = (product, isAdditionalPrice = false) => {
  var _a, _b, _c, _d, _e, _f;
  if (((_b = (_a = product.display) == null ? void 0 : _a.price) == null ? void 0 : _b.enabled) === false) {
    return { type: "HiddenPrice" };
  }
  if (product.id === PRODUCT_ID.LITE_PRODUCT_ID && product.purchaseState === CatalogueProductPurchaseState.PURCHASED) {
    return {
      type: "FixedPrice",
      retailPrice: { currency: Currency.AUD, value: 0 },
      netPrice: { currency: Currency.AUD, value: 0 },
      tax: void 0,
      retailTax: void 0,
      isAdditionalPrice: false
    };
  }
  if ("pricing" in product && product.pricing && "unitPrice" in product.pricing && "cappedPrice" in product.pricing && product.pricing.unitPrice && "netPrice" in product.pricing.unitPrice && product.pricing.cappedPrice && "netPrice" in product.pricing.cappedPrice) {
    return {
      type: "CappedUnitPrice",
      // @TODO: read from product.pricing.metadata.unitType
      unitType: "application",
      maxBillableUnits: product.pricing.maxBillableUnits,
      unitPrice: {
        type: "FixedPrice",
        retailPrice: product.pricing.unitPrice.retailPrice,
        netPrice: product.pricing.unitPrice.netPrice,
        tax: product.pricing.unitPrice.tax ?? void 0,
        retailTax: product.pricing.unitPrice.retailTax ?? void 0,
        isAdditionalPrice
      },
      cappedPrice: {
        type: "FixedPrice",
        retailPrice: product.pricing.cappedPrice.retailPrice,
        netPrice: product.pricing.cappedPrice.netPrice,
        tax: product.pricing.cappedPrice.tax ?? void 0,
        retailTax: product.pricing.cappedPrice.retailTax ?? void 0,
        isAdditionalPrice
      },
      variant: mapPriceVariant(((_d = (_c = product.display) == null ? void 0 : _c.price) == null ? void 0 : _d.variant) ?? void 0)
    };
  }
  if (product.price && "netPrice" in product.price) {
    return {
      type: "FixedPrice",
      retailPrice: product.price.retailPrice,
      netPrice: product.price.netPrice,
      tax: product.price.tax ?? void 0,
      retailTax: product.price.retailTax ?? void 0,
      isAdditionalPrice,
      variant: mapPriceVariant(((_f = (_e = product.display) == null ? void 0 : _e.price) == null ? void 0 : _f.variant) ?? void 0)
    };
  }
  return { type: "HiddenPrice" };
};
const mapPurchaseState = (purchaseState) => purchaseState ?? "PURCHASABLE";
const RatingVariants = ["circles", "bar-chart", "pie-chart"];
const RatingSizes = ["medium", "small"];
const isRatingSize = (size) => RatingSizes.includes(size);
const mapRatingSize = (size) => size && isRatingSize(size) ? size : void 0;
const isRatingVariant = (variant) => RatingVariants.some((ratingVariant) => ratingVariant === variant);
const mapRatingVariant = (variant) => variant && isRatingVariant(variant) ? variant : void 0;
const mapRating = (rating) => {
  if (rating == null ? void 0 : rating.enabled) {
    return {
      value: rating.value,
      variant: mapRatingVariant(rating.variant ?? void 0),
      size: mapRatingSize(rating.size ?? void 0)
    };
  }
  return void 0;
};
const DEFAULT_VARIANT$1 = "default";
const TaglineVariants = ["default", "featured", "bolded"];
const isTaglineVariant = (variant) => TaglineVariants.some((taglineVariant) => taglineVariant === variant);
const mapTaglineVariant = (variant) => variant && isTaglineVariant(variant) ? variant : DEFAULT_VARIANT$1;
const mapTagline = (tagline) => {
  if (tagline == null ? void 0 : tagline.enabled) {
    const variant = mapTaglineVariant(tagline.variant ?? void 0);
    return { variant, content: tagline.content };
  }
  return void 0;
};
const DEFAULT_VARIANT = "default";
const titleVariants = ["default", "small"];
const isTitleVariant = (variant) => titleVariants.some((titleVariant) => variant === titleVariant);
const mapTitleVariant = (variant) => variant && isTitleVariant(variant) ? variant : DEFAULT_VARIANT;
const mapTitle = (title) => {
  if (title == null ? void 0 : title.enabled) {
    const variant = mapTitleVariant(title.variant ?? void 0);
    return { content: title.content, variant };
  }
  return void 0;
};
const mapType = (typename) => typename ?? "UnknownProduct";
const mapUpgradePaths = (upgradePaths, mapProduct2) => {
  if (!upgradePaths) {
    return [];
  }
  return upgradePaths.map(
    ({
      targetProduct2,
      upgradeProduct
    }) => {
      const targetProduct = targetProduct2;
      const isValidUpgrade = targetProduct.__typename === "AdProduct" || targetProduct.__typename === "AdProductUpgrade";
      if (!isValidUpgrade) {
        return void 0;
      }
      const isAdditionalPrice = true;
      const data = mapProduct2(upgradeProduct);
      const { id } = targetProduct;
      const { id: upgradeProductId } = upgradeProduct;
      const name = mapName(targetProduct.displayMetadata, targetProduct.name);
      const price = mapPrice(upgradeProduct, isAdditionalPrice);
      const type = targetProduct.__typename === "AdProduct" ? "AdProduct" : "AdProductUpgrade";
      return { ...data, id, name, price, upgradeProductId, type };
    }
  ).filter(
    (upgradePath) => upgradePath !== void 0
  );
};
const isAdProductAddOn = (product) => product.__typename === "AdProductAddOn";
const sortProducts = (products = [], sortedIds = []) => {
  const indexMap = new Map(sortedIds.map((id, index) => [id, index]));
  const maxIndex = Number.MAX_SAFE_INTEGER;
  const cloneProducts = Array.from(products);
  return cloneProducts.sort(
    (a, b) => (indexMap.get(a.id) ?? maxIndex) - (indexMap.get(b.id) ?? maxIndex)
  );
};
const mapProduct = (product) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
  const baseProduct = {
    id: product.id,
    purchaseState: mapPurchaseState(product.purchaseState),
    type: mapType(product.__typename),
    name: mapName(product.displayMetadata, product.name),
    panel: mapPanel((_a = product.display) == null ? void 0 : _a.panel),
    // TODO refactor this function
    price: mapPrice(product),
    title: mapTitle((_b = product.display) == null ? void 0 : _b.title),
    banner: mapBanner((_c = product.display) == null ? void 0 : _c.banner),
    tagline: mapTagline((_d = product.display) == null ? void 0 : _d.tagline),
    custom: mapCustom((_e = product.display) == null ? void 0 : _e.custom),
    inclusions: mapInclusions((_f = product.display) == null ? void 0 : _f.inclusions),
    cta: mapCta((_g = product.display) == null ? void 0 : _g.cta),
    image: mapImage$1((_h = product.display) == null ? void 0 : _h.image),
    rating: mapRating((_i = product.display) == null ? void 0 : _i.rating),
    comparatives: mapComparatives((_j = product.display) == null ? void 0 : _j.comparatives)
  };
  if (isAdProductAddOn(product)) {
    return {
      ...baseProduct,
      upgradePaths: [],
      addOns: []
    };
  }
  const expectations = mapExpectations({
    expectations: (_k = product.display) == null ? void 0 : _k.expectations,
    expectationForDraft: product.expectationForDraft,
    expectationForJob: product.expectationForJob,
    comparatives: (_l = product.display) == null ? void 0 : _l.comparatives
  });
  if ((_n = (_m = baseProduct.comparatives) == null ? void 0 : _m.content) == null ? void 0 : _n.length) {
    baseProduct.comparatives.content = (_p = (_o = baseProduct.comparatives) == null ? void 0 : _o.content) == null ? void 0 : _p.map((comparative) => {
      if (comparative.key === "applications") {
        return typeof (expectations == null ? void 0 : expectations.count) === "number" ? {
          ...comparative,
          content: expectations.count.toString()
        } : void 0;
      }
      return comparative;
    }).filter((comparative) => typeof comparative !== "undefined");
  }
  return {
    ...baseProduct,
    expectations,
    upgradePaths: mapUpgradePaths(product.upgradePaths, mapProduct),
    addOns: mapAddOns(product.addOns)
  };
};
const mapProducts = (products, layoutDisplayRules) => {
  var _a;
  if (!(products == null ? void 0 : products.length)) return [];
  const mappedProducts = products.map((product) => mapProduct(product));
  if (!layoutDisplayRules) return mappedProducts;
  return sortProducts(mappedProducts, layoutDisplayRules.sortProductIds).slice(0, (_a = layoutDisplayRules.productCap) == null ? void 0 : _a.products).map(
    (product) => {
      var _a2;
      return (
        // Return the product with upgrades capped to maximum amount defined
        {
          ...product,
          upgradePaths: product.upgradePaths.slice(
            0,
            (_a2 = layoutDisplayRules.productCap) == null ? void 0 : _a2.upgrades
          )
        }
      );
    }
  );
};
const prepareCompareButtonEvent = (data) => ({
  tracking: {
    eventName: `compare_button_${data.action}`
  }
});
const getCurrentProducts = (meta) => {
  var _a;
  const purchasedAdProduct = (_a = meta == null ? void 0 : meta.products) == null ? void 0 : _a.find(
    (product) => product.purchaseState === "PURCHASED"
  );
  if (!purchasedAdProduct) {
    return [];
  }
  const purchasedAddOnProducts = purchasedAdProduct.addOns.filter(
    (product) => product.purchaseState === "PURCHASED"
  ) ?? [];
  return [purchasedAdProduct, ...purchasedAddOnProducts];
};
const mapProductsForDisplayEvent = (products) => products == null ? void 0 : products.filter((product) => {
  var _a;
  return ((_a = product.panel) == null ? void 0 : _a.variant) !== "hidden";
}).map((product) => {
  let productId = product.id;
  if (product.type === "AdProductUpgrade" && product.upgradeProductId) {
    productId = product.upgradeProductId;
  }
  const netPriceInput = product.price.type === "CappedUnitPrice" ? product.price.unitPrice : product.price;
  const { displayNetPrice: netPrice } = netPriceInput.type === "FixedPrice" ? getDisplayPrices(netPriceInput) : { displayNetPrice: void 0 };
  const { displayNetPrice: cappedPrice } = product.price.type === "CappedUnitPrice" && product.price.cappedPrice.type === "FixedPrice" ? getDisplayPrices(product.price.cappedPrice) : { displayNetPrice: void 0 };
  const expectation = product.expectations ? {
    ...typeof product.expectations.count === "number" ? { applicationsCompleted: product.expectations.count } : {},
    correlationId: product.expectations.correlationId
  } : void 0;
  return {
    productId,
    netPrice: netPrice == null ? void 0 : netPrice.price,
    ...cappedPrice ? { cappedPrice: cappedPrice.price } : void 0,
    ...expectation ? { expectation } : void 0
  };
});
const mapProductPricingForDisplayEvent = (products) => {
  const productPricing = products.map((product, index) => {
    var _a;
    return {
      position: index + 1,
      id: product.id,
      price: (product.price.type === "FixedPrice" ? product.price.netPrice.value : void 0) ?? -1,
      currency: product.price.type === "FixedPrice" ? product.price.netPrice.currency : void 0,
      priceHidden: typeof product.price === "undefined",
      ...((_a = product.expectations) == null ? void 0 : _a.unit) === "applications" && typeof product.expectations.count === "number" && {
        adProductsExpectedApps: product.expectations.count
      }
    };
  });
  return productPricing;
};
const prepareDisplayEvent = (data, meta) => {
  var _a, _b;
  const hubble = {
    eventName: "ad_products_displayed",
    eventData: {
      widgetName: "AdSelection",
      products: mapProductsForDisplayEvent(meta.products)
    }
  };
  const products = [
    ...(meta == null ? void 0 : meta.products) ?? [],
    ...((_a = meta == null ? void 0 : meta.products) == null ? void 0 : _a.flatMap(({ upgradePaths }) => upgradePaths)) ?? []
  ].filter((product) => typeof product !== "undefined");
  const currentProducts = getCurrentProducts(meta);
  const hasCurrentProducts = currentProducts.length > 0;
  const tracking = {
    eventName: "select_ad_widget_displayed",
    adProductSetId: (_b = meta.context) == null ? void 0 : _b.products.productSetId,
    productPricing: mapProductPricingForDisplayEvent(products),
    ...hasCurrentProducts && {
      currentProducts: currentProducts.map(({ id }) => ({ id }))
    }
  };
  return { tracking, hubble };
};
const prepareUpsellsDisplayEvent = (data, meta) => {
  var _a;
  const hubble = {
    eventName: "upsell_products_displayed",
    eventData: {
      widgetName: "AdSelection",
      selectedProductId: meta.selectedParentProductId,
      products: mapProductsForDisplayEvent([
        ...data.upgradePaths,
        ...data.addOns
      ])
    }
  };
  const tracking = {
    eventName: "select_ad_widget_upsell_displayed",
    adProductSetId: (_a = meta.context) == null ? void 0 : _a.products.productSetId,
    productPricing: mapProductPricingForDisplayEvent([
      ...data.upgradePaths,
      ...data.addOns
    ])
  };
  return { tracking, hubble };
};
const prepareSelectionEvent = (data, meta) => {
  var _a, _b, _c, _d;
  const productId = (_a = meta.product) == null ? void 0 : _a.id;
  const adProductsExpectedApps = ((_c = (_b = meta.product) == null ? void 0 : _b.expectations) == null ? void 0 : _c.unit) === "applications" ? meta.product.expectations.count : null;
  const hubble = {
    eventName: "ad_product_selected",
    eventData: {
      widgetName: "AdSelection",
      productId
    }
  };
  const tracking = {
    eventName: "ad_type_pressed",
    productEvents: ["add"],
    productSetProductIds: [productId],
    adProductName: [(_d = meta.product) == null ? void 0 : _d.name.toLowerCase()],
    ...adProductsExpectedApps && {
      adProductsExpectedApps
    }
  };
  return { tracking, hubble };
};
const prepareUpsellSelectionEvent = (data, meta) => {
  var _a, _b;
  const productId = (_a = meta.product) == null ? void 0 : _a.id;
  const hubble = {
    eventName: "upsell_product_selected",
    eventData: {
      widgetName: "AdSelection",
      selectedProductId: meta.selectedParentProductId,
      productId
    }
  };
  const tracking = {
    eventName: "select_ad_widget_upsell_selected",
    adProductSetId: (_b = meta.context) == null ? void 0 : _b.products.productSetId,
    selectedProductId: meta.selectedParentProductId,
    productId,
    linkAction: data.action === "added" ? "select" : "remove"
  };
  switch (data.action) {
    case "removed":
      return { tracking };
    case "added":
    default:
      return { tracking, hubble };
  }
};
const prepareTooltipEvent = (data, meta) => {
  var _a, _b, _c;
  const tracking = {
    eventName: "tooltip_pressed",
    tooltipType: (_a = data == null ? void 0 : data.content) == null ? void 0 : _a.replace(/[.,]/g, "").replace(/[\s_]+/g, "-").toLowerCase(),
    adProductName: [(_b = meta.product) == null ? void 0 : _b.name],
    productSetProductIds: [(_c = meta.product) == null ? void 0 : _c.id]
  };
  return { tracking };
};
const prepareTrackingData = (data, meta) => {
  var _a;
  if (data.type === "cta" && (data.action === "added" || data.action === "removed") && ((_a = meta.product) == null ? void 0 : _a.type) === "AdProductAddOn") {
    return prepareUpsellSelectionEvent(data, meta);
  }
  if (data.type === "cta" && data.action === "clicked") {
    return prepareSelectionEvent(data, meta);
  }
  if (data.type === "products" && data.action === "displayed") {
    return prepareDisplayEvent(data, meta);
  }
  if (data.type === "upsells" && data.action === "displayed") {
    return prepareUpsellsDisplayEvent(data, meta);
  }
  if (data.type === "tooltip" && data.action === "opened") {
    return prepareTooltipEvent(data, meta);
  }
  if (data.type === "compare" && (data.action === "opened" || data.action === "closed")) {
    return prepareCompareButtonEvent(data);
  }
};
const getLayoutComponent = (layoutDisplayRules) => {
  var _a, _b;
  if ((_a = layoutDisplayRules == null ? void 0 : layoutDisplayRules.format) == null ? void 0 : _a.startsWith("row")) {
    return RowLayout;
  }
  if ((_b = layoutDisplayRules == null ? void 0 : layoutDisplayRules.format) == null ? void 0 : _b.startsWith("grid")) {
    return GridLayout;
  }
  return GridLayout;
};
const ProductSelection = ({
  layoutDisplay,
  products,
  selectedProductIds,
  loadingProductIds,
  onProductSelect = () => {
  },
  context,
  onTrackingEvent,
  layout
}) => {
  const layoutDisplayRules = layoutDisplay ? mapLayoutDisplay(layoutDisplay) : void 0;
  const LayoutComponent = getLayoutComponent(layoutDisplayRules);
  return /* @__PURE__ */ jsx(ProductSelectionContext.Provider, { value: context, children: /* @__PURE__ */ jsx(
    TrackingContext.Provider,
    {
      value: {
        trackEvent: (data, inputMeta) => {
          const meta = { ...inputMeta, context };
          const output = prepareTrackingData(data, meta);
          if (output && onTrackingEvent) {
            onTrackingEvent(output);
          }
        }
      },
      children: /* @__PURE__ */ jsx(
        LayoutComponent,
        {
          layoutDisplayRules,
          products: mapProducts(products, layoutDisplayRules),
          selectedProductIds,
          loadingProductIds,
          onProductSelect,
          prefixContent: layout == null ? void 0 : layout.prefixContent,
          hideDisclaimer: layout == null ? void 0 : layout.hideDisclaimer
        }
      )
    }
  ) });
};
export {
  ProductSelection
};
